import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
	ShoppingCart, Menu, X, User, LogOut, Heart, Package, Lock, UserPlus, LogIn,
	ChevronDown, Star, Crown, Gem, Circle, Diamond
} from "lucide-react";
import { useUserStore } from "../stores/useUserStore";
import { useCartStore } from "../stores/useCartStore";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
    const { user, logout } = useUserStore();
    const { cart } = useCartStore();
	// states שהוסרו לשמירת ניקיון קוד (לא היו בשימוש)
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [activeMenu, setActiveMenu] = useState(null);
	const [hoveredItem, setHoveredItem] = useState(null);
	const location = useLocation();
    // navigate הוסר כי אינו בשימוש ישיר כרגע
	const menuRef = useRef(null);
	const navRef = useRef(null);

	// מבנה ניווט מתקדם עם תת-קטגוריות
	const navigationStructure = {
		necklaces: {
			title: 'שרשראות',
			icon: <Circle className="w-5 h-5 " />,
			path: '/category/necklaces',
			description: 'שרשראות בעבודת יד עם אבני חן טבעיות',
			subcategories: [
				{
					title: 'שרשראות אבני חן',
					icon: <Gem className="w-4 h-4" />,
					path: '/category/necklaces?subcategory=gemstone',
					description: 'שרשראות בעבודת יד עם אבני חן טבעיות כמו טייגר איי, אמטיסט ולברדורייט',
					items: ['שרשרת טייגר איי', 'שרשרת אמטיסט', 'שרשרת לברדורייט']
				},
				{
					title: 'שרשראות יוקרה',
					icon: <Crown className="w-4 h-4" />,
					path: '/category/necklaces?subcategory=luxury',
					description: 'שרשראות יוקרתיות עם אבנים נדירות כמו רובי וספיר',
					items: ['שרשרת רובי גולדפילד', 'שרשרת קורל אדום', 'שרשרת ברונזייט']
				},
				{
					title: 'שרשראות פלדת אל חלד',
					icon: <Circle className="w-4 h-4" />,
					path: '/category/necklaces?subcategory=steel',
					description: 'שרשראות פלדת אל חלד מושחרות בעיצוב מודרני',
					items: ['שרשרת כסף מושחר', 'שרשרת פלדה יוקרתית', 'שרשרת מודרנית']
				},
				{
					title: 'שרשראות יומיומיות',
					icon: <Heart className="w-4 h-4" />,
					path: '/category/necklaces?subcategory=daily',
					description: 'שרשראות נוחות ויפות לשימוש יומיומי',
					items: ['שרשרת היולייט', 'שרשרת אמזונייט', 'שרשרת מלכית']
				}
			]
		},
		earrings: {
			title: 'עגילים',
			icon: <Diamond className="w-5 h-5" />,
			path: '/category/earrings',
			description: 'עגילי פלדת אל חלד באיכות יוקרתיתי',
			subcategories: [
				{
				title: 'עגילים גאומטריים',
				icon: <Circle className="w-4 h-4" />,
				path: '/category/earrings?subcategory=geometric',
				description: 'עגילים בעיצוב גאומטרי מודרני וסטייל',
				items: ['עגילי סטייל גאומטרית', 'עגילי שבט המאיה', 'עגילי לב']
				},
				{
				title: 'עגילי אבני חן',
				icon: <Gem className="w-4 h-4" />,
				path: '/category/earrings?subcategory=stone',
				description: 'עגילים עם אבני חן טבעיות ואבנים יקרות',
				items: ['עגילי זירקון', 'עגילי אוניקס', 'עגילי אבן טבעית']
				},
				{
				title: 'עגילי זהב',
				icon: <Star className="w-4 h-4" />,
				path: '/category/earrings?subcategory=gold',
				description: 'עגילים בגוון זהב וזהב לבן אלגנטי',
				items: ['עגילי זהב לבן', 'עגילי כוכב זהב', 'עגילי חמסה זהב']
				},
				{
				title: 'עגילים יומיומיים',
				icon: <Heart className="w-4 h-4" />,
				path: '/category/earrings?subcategory=daily',
				description: 'עגילים פשוטים ונוחים לשימוש יומיומי',
				items: ['עגילי עוגן', 'עגילי באטמן', 'עגילי דולפין']
				}
			]
		},
		bracelets: {
			title: 'צמידים',
			icon: <Package className="w-5 h-5" />,
			path: '/category/bracelets',
			description: 'מגוון גדול של צמידים איכותיים',
			subcategories: [
				{
				title: 'צמידי אבני חן',
				icon: <Gem className="w-4 h-4" />,
				path: '/category/bracelets?subcategory=stone',
				description: 'צמידים עם אבני חן טבעיות ואבנים יקרות',
				items: ['צמיד טייגר איי', 'צמיד אובסידיאן', 'צמיד לאפיס']
				},
				{
				title: 'צמידי פלדה',
				icon: <Circle className="w-4 h-4" />,
				path: '/category/bracelets?subcategory=steel',
				description: 'צמידי פלדת אל חלד בעיצוב מודרני',
				items: ['צמיד פלדה כסף', 'צמיד פלדה מודרני', 'צמיד פלדה קלאסי']
				},
				{
				title: 'צמידים טבעיים',
				icon: <Heart className="w-4 h-4" />,
				path: '/category/bracelets?subcategory=natural',
				description: 'צמידים בעבודת יד עם חומרים טבעיים',
				items: ['צמיד חוט סיליקון', 'צמיד חוט ניילון', 'צמיד בעבודת יד']
				},
				{
				title: 'צמידי יוקרה',
				icon: <Crown className="w-4 h-4" />,
				path: '/category/bracelets?subcategory=luxury',
				description: 'צמידים יוקרתיים במחירים גבוהים',
				items: ['צמידים פרימיום', 'צמידים מוגבלים', 'צמידי יוקרה']
				}
			]
		},
		collections: {
			title: 'מוצרים מומלצים',
			icon: <Star className="w-5 h-5" />,
			path: '/category/necklaces?featured=true',
			description: 'המוצרים המומלצים שלנו - 22 מוצרים נבחרים',
			subcategories: [
				{
					title: 'שרשראות מומלצות',
					icon: <Gem className="w-4 h-4" />,
					path: '/category/necklaces?featured=true',
					description: 'השרשראות הכי פופולריות בקולקציה שלנו',
					items: ['שרשרת אמזונייט', 'שרשרת היולייט וסודולייט', 'שרשרת טייגר איי']
				},
				{
					title: 'עגילים מומלצים',
					icon: <Heart className="w-4 h-4" />,
					path: '/category/earrings?featured=true',
					description: 'העגילים הנבחרים והפופולריים ביותר',
					items: ['עגילי קונכייה', 'עגילי אוניקס משולשים', 'עגילים קלאסיים']
				},
				{
					title: 'צמידים מומלצים',
					icon: <Crown className="w-4 h-4" />,
					path: '/category/bracelets?featured=true',
					description: 'הצמידים הטובים ביותר במבחר שלנו',
					items: ['צמידי אבני חן בעבודת יד', 'צמידים יוקרתיים', 'צמידים קלאסיים']
				}
			]
		}
	};

	// מספר הפריטים בעגלה
	const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);

	// סגירת תפריט נייד כשמשנים דף
    useEffect(() => {
        setIsMenuOpen(false);
        setActiveMenu(null);
        setHoveredItem(null);
    }, [location]);

	// סגירת תפריט כשלוחצים מחוץ לו
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (navRef.current && !navRef.current.contains(event.target)) {
				setActiveMenu(null);
				setHoveredItem(null);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// פונקציית התנתקות
	// פונקציית התנתקות נשמרת לשימוש עתידי בתפריט משתמש

	// פונקציות לניהול תפריט נייד
	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const closeMenu = () => {
		setIsMenuOpen(false);
	};

	// פונקציות לניהול תפריט מתקדם
	const handleMenuEnter = (menuKey) => {
		setActiveMenu(menuKey);
	};

	const handleMenuLeave = () => {
		setActiveMenu(null);
		setHoveredItem(null);
	};

	// בדיקה אם הנתיב פעיל
	const isActivePath = (path) => {
		if (path === '/') return location.pathname === '/';
		return location.pathname.startsWith(path);
	};

	return (
		<header className='fixed top-0 left-0 w-full bg-[var(--nav-bg)] opacity-90 backdrop-blur-md shadow-xl z-40 transition-all duration-300 ' style={{borderBottomColor: 'var(--brand-primary)'}} dir="rtl">
			<div className='container  justify-between mx-auto px-2 py-2' ref={menuRef}>
				<div className='flex justify-between items-center'>
					{/* Logo */}
					<Link to='/' className='flex items-center'>
						<img 
							src="./logo.png" 
							alt="SHA-SHA By Nataly" 
							className="h-10 min-w-12 sm:h-12 md:h-14 lg:h-16 xl:h-20 bg-white drop-shadow-sm hover:drop-shadow-md transition-all rounded-2xl duration-300"
						/>
					</Link>

					{/* Advanced Desktop Navigation */}
					<nav 
						ref={navRef}
						className="hidden md:flex items-center gap-3 lg:gap-6 xl:gap-8 relative"
						dir="rtl"
					>
						{Object.entries(navigationStructure).map(([key, category]) => (
							<div
								key={key}
								className="relative"
								onMouseEnter={() => handleMenuEnter(key)}
								onMouseLeave={handleMenuLeave}
							>
								{/* כפתור קטגוריה ראשית */}
								<Link
									to={category.path}
									className={`
										flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300
										${isActivePath(category.path)
											? 'text-[var(--text-on-dark)] bg-[color:var(--brand-primary-light)]'
											: 'text-[var(--text-on-dark)] hover:text-[#edebe7] hover:bg-[color:var(--brand-primary-medium)]'
										}
									`}
								>
									{category.icon}
									<span className="font-medium">{category.title}</span>
									<ChevronDown 
										className={`w-4 h-4 transition-transform duration-300 ${
											activeMenu === key ? 'rotate-180' : ''
										}`} 
									/>
								</Link>

								{/* Mega Menu */}
								<AnimatePresence>
									{activeMenu === key && (
										<motion.div
											initial={{ opacity: 0, y: -10, scale: 0.95 }}
											animate={{ opacity: 1, y: 0, scale: 1 }}
											exit={{ opacity: 0, y: -10, scale: 0.95 }}
											transition={{ duration: 0.2, ease: "easeOut" }}
											className="absolute top-full right-0 mt-2 w-[600px] bg-white border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden"
										>
											<div className="p-6">
												{/* כותרת הקטגוריה */}
												<div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-700">
													<div className="p-3 bg-brand-primary-light rounded-lg">
														{category.icon}
													</div>
													<div>
														<h3 className="text-xl font-bold text-white">{category.title}</h3>
														<p className="text-gray-400 text-sm">{category.description}</p>
													</div>
												</div>

												{/* תת-קטגוריות */}
												<div className="grid grid-cols-2 gap-6">
													{category.subcategories.map((subcat, index) => (
														<motion.div
															key={index}
															className="relative"
															onMouseEnter={() => setHoveredItem(`${key}-${index}`)}
															onMouseLeave={() => setHoveredItem(null)}
														>
															<Link
																to={subcat.path}
																className={`
																	block p-4 rounded-lg border transition-all duration-300
										${hoveredItem === `${key}-${index}`
											? 'border-[var(--brand-primary)]/50 bg-[color:var(--brand-primary-light)] transform scale-105'
											: 'border-gray-700 bg-[#0e0509]/30 hover:border-gray-500'
										}
																`}
															>
																{/* כותרת תת-קטגוריה */}
																<div className="flex items-center gap-3 mb-2">
																	<div className={`p-2 rounded-lg transition-colors duration-300 ${
																		hoveredItem === `${key}-${index}` 
																			? 'bg-brand-primary-light text-brand-primary-hover' 
																			: 'bg-gray-700 text-gray-300'
																	}`}>
																		{subcat.icon}
																	</div>
																	<div>
																		<h4 className={`font-semibold transition-colors duration-300 ${
																			hoveredItem === `${key}-${index}` 
																				? 'text-brand-primary-hover' 
																				: 'text-white'
																		}`}>
																			{subcat.title}
																		</h4>
																	</div>
																</div>

																{/* תיאור */}
																<p className="text-gray-400 text-sm mb-3 leading-relaxed">
																	{subcat.description}
																</p>

																{/* פריטים דוגמה */}
																<div className="space-y-1">
																	{subcat.items.slice(0, 3).map((item, itemIndex) => (
																		<div 
																			key={itemIndex}
																			className={`flex items-center gap-2 text-xs transition-colors duration-300 ${
																				hoveredItem === `${key}-${index}` 
																					? 'text-brand-primary-hover' 
																					: 'text-gray-500'
																			}`}
																		>
																			<div className={`w-1 h-1 rounded-full transition-colors duration-300 ${
																				hoveredItem === `${key}-${index}` 
																					? 'bg-brand-primary-hover' 
																					: 'bg-gray-500'
																			}`}></div>
																			<span>{item}</span>
																		</div>
																	))}
																</div>
															</Link>
														</motion.div>
													))}
												</div>
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>
						))}
					</nav>

					{/* Right side - Cart (Desktop only) and User Actions */}
					<div className='flex items-center gap-4'>
						{/* Cart - מוצג במסכים מ-iPad ומעלה */}
						<Link
							to={"/cart"}
							className='relative group text-gray-500 hover:text-brand-primary-hover transition duration-300 ease-in-out hidden md:flex'
						>
							<div className='flex items-center gap-1'>
								<ShoppingCart size={24} />
								{cartItemsCount > 0 && (
									<span className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full px-2 py-1 text-xs group-hover:bg-red-600 transition duration-300'>
										{cartItemsCount}
									</span>
								)}
							</div>
						</Link>

						{/* User Actions */}
						{user ? (
							<div className='flex items-center gap-4'>
								{user.role === 'admin' && (
									<Link
										to={"/admin"}
										className='text-gray-500 hover:text-brand-primary-hover transition duration-300 ease-in-out flex items-center gap-1'
									>
										<Lock size={20} />
										<span className="hidden md:inline">ניהול</span>
									</Link>
								)}
								
								<Link
									to={"/profile"}
									className='text-gray-500 hover:text-brand-primary-hover transition duration-300 ease-in-out flex items-center gap-1'
								>
									<User size={20} />
									<span className="hidden sm:inline">פרופיל</span>
								</Link>
								
								<button
									onClick={logout}
									className='text-gray-500 hover:text-brand-primary-hover transition duration-300 ease-in-out flex items-center gap-1'
								>
									<LogOut size={20} />
									<span className="hidden sm:inline">יציאה</span>
								</button>
							</div>
						) : (
							<div className='flex items-center gap-4'>
								<Link
									to={"/signup"}
									className='text-gray-500 hover:text-brand-primary-hover transition duration-300 ease-in-out flex items-center gap-1'
								>
									<UserPlus size={20} />
									<span className="hidden sm:inline">הרשמה</span>
								</Link>
								<Link
									to={"/login"}
									className='text-gray-500 hover:text-brand-primary-hover transition duration-300 ease-in-out flex items-center gap-1'
								>
									<LogIn size={20} />
									<span className="hidden sm:inline">התחברות</span>
								</Link>
							</div>
						)}

						{/* Mobile menu button - מוצג במסכים קטנים מ-11 אינץ' */}
						{user && (
							<button
								className='sm:hidden text-gray-300 hover:text-brand-primary-hover transition duration-300 ease-in-out'
								onClick={toggleMenu}
							>
								{isMenuOpen ? <X size={24} /> : <Menu size={24} />}
							</button>
						)}
					</div>
				</div>

				{/* Mobile Navigation Menu */}
				<AnimatePresence>
					{isMenuOpen && (
						<motion.div
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: 'auto' }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.3 }}
							className='md:hidden mt-4 py-4 border-t border-gray-700'
						>
							<div className='flex flex-col gap-3'>
								{user && (
									<>
										{/* Mobile Cart */}
										<Link
											to="/cart"
											className="flex items-center gap-3 px-4 py-3 text-gray-500 hover:text-brand-primary-hover hover:bg-gray-800 rounded-lg transition duration-300"
											onClick={closeMenu}
										>
											<ShoppingCart className='text-xl' size={20} />
											<span className="text-lg font-medium">עגלת קניות</span>
											{cartItemsCount > 0 && (
												<span className="bg-brand-primary text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold">
													{cartItemsCount}
												</span>
											)}
										</Link>
									</>
								)}
								<Link
									to="/about"
									className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-brand-primary-hover hover:bg-gray-800 rounded-lg transition duration-300"
									onClick={closeMenu}
								>
									<span className="text-lg font-medium">אודות</span>
								</Link>
								<Link
									to="/contact"
									className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-brand-primary-hover hover:bg-gray-800 rounded-lg transition duration-300"
									onClick={closeMenu}
								>
									<span className="text-lg font-medium">צור קשר</span>
								</Link>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</header>
	);
};

export default Navbar;
